import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Share2, Check } from 'lucide-react';
import { Message } from '../../types';

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(message.content.slice(0, 4000));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className={`flex gap-2 sm:gap-3 mb-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm sm:text-lg flex-shrink-0 ${
          isUser
            ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-md'
            : 'bg-white border border-gray-200 text-gray-600 shadow-sm'
        }`}
      >
        {isUser ? '👤' : '⚖️'}
      </div>

      <div className={`max-w-[85%] sm:max-w-[75%] group relative`}>
        <div
          className={`rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 ${
            isUser
              ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-md'
              : 'bg-white shadow-sm border border-gray-200 text-gray-900'
          }`}
        >
          {isUser ? (
            <div className="text-[13px] sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </div>
          ) : (
            <div className="text-[12px] sm:text-[13px] leading-[1.7] break-words max-w-none text-gray-800" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h2 className="text-[14px] sm:text-[15px] font-bold text-gray-900 mt-4 mb-2 pb-1 border-b-2 border-gray-800 uppercase tracking-wide">{children}</h2>,
                  h2: ({ children }) => <h3 className="text-[13px] sm:text-[14px] font-bold text-gray-900 mt-4 mb-1.5 pb-0.5 border-b border-gray-300">{children}</h3>,
                  h3: ({ children }) => <h4 className="text-[12px] sm:text-[13px] font-bold text-gray-800 mt-3 mb-1">{children}</h4>,
                  h4: ({ children }) => <h5 className="text-[12px] sm:text-[13px] font-semibold text-gray-700 mt-2 mb-0.5 italic">{children}</h5>,
                  p: ({ children }) => <p className="my-1.5 sm:my-2 text-[12px] sm:text-[13px] leading-[1.7] text-gray-800">{children}</p>,
                  ul: ({ children }) => <ul className="my-2 pl-4 sm:pl-5 space-y-1 list-disc marker:text-gray-400">{children}</ul>,
                  ol: ({ children }) => <ol className="my-2 pl-4 sm:pl-5 space-y-1 list-decimal marker:text-gray-600 marker:font-semibold">{children}</ol>,
                  li: ({ children }) => <li className="text-[12px] sm:text-[13px] leading-[1.6] pl-0.5 sm:pl-1">{children}</li>,
                  strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                  em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                  table: ({ children }) => (
                    <div className="my-3 overflow-x-auto rounded-lg border border-gray-300 shadow-sm -mx-1 sm:mx-0">
                      <table className="w-full text-[10px] sm:text-[11px] border-collapse min-w-[400px]">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => <thead className="bg-slate-800 text-white">{children}</thead>,
                  th: ({ children }) => <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left font-semibold text-[10px] sm:text-[11px] uppercase tracking-wider border-r border-slate-700 last:border-r-0">{children}</th>,
                  tbody: ({ children }) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
                  tr: ({ children }) => <tr className="hover:bg-gray-50 transition-colors">{children}</tr>,
                  td: ({ children }) => <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-[11px] text-gray-700 border-r border-gray-100 last:border-r-0 align-top">{children}</td>,
                  code: ({ className, children }) => {
                    const isBlock = className?.includes('language-');
                    if (isBlock) {
                      return <pre className="my-3 p-3 sm:p-4 bg-slate-900 text-slate-200 rounded-lg text-[10px] sm:text-[11px] overflow-x-auto font-mono leading-relaxed"><code>{children}</code></pre>;
                    }
                    return <code className="px-1 sm:px-1.5 py-0.5 bg-amber-50 text-amber-800 rounded text-[10px] sm:text-[11px] font-semibold border border-amber-200">{children}</code>;
                  },
                  blockquote: ({ children }) => <blockquote className="my-3 pl-3 sm:pl-4 py-2 border-l-4 border-indigo-500 bg-indigo-50/50 text-gray-700 italic rounded-r-lg">{children}</blockquote>,
                  hr: () => <hr className="my-4 border-t-2 border-gray-300" />,
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-medium underline decoration-indigo-300 hover:text-indigo-800">{children}</a>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Action buttons — only for AI messages */}
        {!isUser && message.content.length > 20 && (
          <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 sm:transition-opacity">
            <button onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
              {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={handleWhatsApp}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors">
              <Share2 size={11} /> WhatsApp
            </button>
            <span className="text-[9px] text-gray-400 ml-1">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        {/* Timestamp for user messages */}
        {isUser && (
          <div className="text-[9px] text-gray-400 mt-1 text-right">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
